<template lang="pug">
    div
        b-modal(
            v-model="modalShow"
            @shown="getCipTemplate"
            @hidden="onHidden"
            modal-class="dsg-modal"
            id="modal-sm"
            size="lg"
            :title="modalTitle"
            centered
            no-close-on-backdrop
        )
            <!-- loading -->
            .dsg-empty(v-if="isLoading")
                b-spinner(variant="primary")
                p.mb-0.mt-2 Loading presets…
            <!-- content -->
            template(v-else)
                .dsg-citizen-badge(:class="selectVariant")
                    Icon.mr-1(icon="mdi:account-circle-outline")
                    | {{ getCitizenship() }}
                b-form-group.mb-0
                    template(#label)
                        .dsg-field-label Select template preset
                            span.dsg-req  *
                    b-form-select(v-model="selectedPresetId" :options="presetOptions")
                        template(#first)
                            b-form-select-option(:value="null" disabled) Choose a preset…
                    p.text-muted.small.mt-2.mb-0(v-if="presetOptions.length === 0")
                        Icon.mr-1(icon="mdi:information-outline")
                        | No presets available for this client.
            template(#modal-footer)
                .d-flex.justify-content-end.w-100(style="gap: 0.5rem;")
                    b-button(variant="light" @click="modalShow = false" :disabled="isSubmitting") Cancel
                    b-button.btn-dsg(@click="setPreset" :disabled="isLoading || isSubmitting || !selectedPresetId")
                        b-spinner(v-if="isSubmitting" small)
                        span(v-else) Confirm
</template>

<script>
import dayjs from 'dayjs';
import { Icon } from '@iconify/vue2';

// CIP 2.0 pricing applies to clients admitted on/after this date.
const CIP2_CUTOFF = '2026-07-01';

export default {
    name: 'ModalPrompt',
    components: {
        Icon,
    },
    data() {
        return {
            feeAndSubs: [],
            selectedPresetId: null,
            isLoading: false,
            isSubmitting: false,
        };
    },
    props: {
        showCipPreset: {
            type: Boolean,
            required: true
        },
        client: {
                type: Object,
                required: true,
            },
    },
    computed: {
        selectVariant(){
            return this.client?.crb5c_citizenship === 0 ? 'sg-select' : 'pr-select';
        },
        // CIP 2.0 pricing applies to clients admitted on/after 1-Jul-2026. Keyed off the
        // admission date so existing clients keep their original structure even for follow-ups.
        // NOTE: presets are assigned at onboarding, before any assessment, so a pending client
        // has no admission date yet. Since it is already past the cutoff, a missing admission
        // date means a brand-new client being onboarded now -> treat as CIP 2.0. Existing/old
        // clients always have an admission date (before July), so they resolve to the old set.
        isCip2Client() {
            const adm = this.client?.crb5c_admissiondate;
            if (!adm) return true;
            return !dayjs(adm).isBefore(dayjs(CIP2_CUTOFF), 'day');
        },
        // SG CIP 2.0 rows are split by birth year: "born 1969 or before" vs "born after 1969".
        clientBornBefore1970() {
            const dob = this.client?.crb5c_dob;
            return !!dob && dayjs(dob).year() <= 1969;
        },
        modalTitle() {
            let label = `${this.client.crb5c_no} (${this.getCitizenship()})`;
            if (this.isCip2Client) {
                label += ' · CIP 2.0 (B)';
                if (this.client.crb5c_citizenship === 0) {
                    label += this.clientBornBefore1970 ? ' · born ≤1969' : ' · born >1969';
                }
            }
            return label;
        },
        presetOptions() {
            const isSg = this.client.crb5c_citizenship === 0;
            const isCip2 = this.isCip2Client;
            const bornBefore = this.clientBornBefore1970;
            return this.feeAndSubs
                .filter(item => {
                    // Generation gate: CIP 2.0 clients see only iscip2 rows; others keep the rest.
                    if (isCip2 ? item.crb5c_iscip2 !== true : item.crb5c_iscip2 === true) return false;
                    if (isSg) {
                        if (item.crb5c_for_citizen !== true) return false;
                        if (isCip2 && item.crb5c_born1969orbefore !== bornBefore) return false;
                        return true;
                    }
                    return item.crb5c_for_pr === true;
                })

                .sort((a, b) => {
                    const pa = Number(a.crb5c_subsidypercentage);
                    const pb = Number(b.crb5c_subsidypercentage);
                    const na = Number.isNaN(pa) ? -Infinity : pa;
                    const nb = Number.isNaN(pb) ? -Infinity : pb;
                    if (na !== nb) return nb - na;
                    return String(a.crb5c_description || '').localeCompare(String(b.crb5c_description || ''));
                })
                .map(item => ({
                    text: item.crb5c_description,
                    value: item.crb5c_fow_feeid
                }));
        },
        modalShow: {
        get() {
            return this.showCipPreset;
        },
        set(value) {
            this.$emit('update:showCipPreset', value);
        }
    },
    },
    methods: {
        getCitizenship() {
            return this.client?.crb5c_citizenship === 0 ? 'Singaporean' : 'PR';
        },
        async setPreset() {
            if (!this.selectedPresetId) {
                this.$bvToast.toast('Please select a preset.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            this.isSubmitting = true;
            try {
                const payload = {
                    feeId: this.selectedPresetId,
                    clientId: this.client.crb5c_fow_customerid
                };
                await this.$store.state.api.patch(`/cip`, payload);
                this.$bvToast.toast('Preset applied successfully.', {
                    title: 'Success',
                    variant: 'success',
                    solid: true
                });
                this.$emit('update:showCipPreset', false);
            } catch (error) {
                console.error('Error applying preset:', error);
                this.$bvToast.toast('Failed to apply preset.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        async getCipTemplate(){
            this.isLoading = true;
            try {
                const { data } = await this.$store.state.api.get(`/cip`);
                this.feeAndSubs = data || [];
                if(this.client?.crb5c_CIPPreset?.crb5c_fow_feeid){
                    this.selectedPresetId = this.client.crb5c_CIPPreset.crb5c_fow_feeid;
                } else {
                    this.selectedPresetId = null;
                }
            } catch (error) {
                console.error('Error loading CIP presets:', error);
                this.feeAndSubs = [];
                this.$bvToast.toast('Failed to load presets.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isLoading = false;
            }
        },
        onHidden(){
            this.selectedPresetId = null;
            this.feeAndSubs = [];
        }
    },

}
</script>

<style lang="scss">
// small citizenship pill shown above the preset selector
.dsg-citizen-badge {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  margin-bottom: 1rem;

  &.sg-select {
    background-color: #fdeafc;
    color: #a83fa0;
  }
  &.pr-select {
    background-color: #e4f1f8;
    color: #2b7aa5;
  }
}
</style>